import { Box, IconButton, Typography, useTheme, Button, Avatar, Tooltip, MenuItem, Menu, Divider, keyframes } from "@mui/material";
import { useContext, useState } from "react";
import { useAppContext } from "../../contexts/AppContext";
import CircleIcon from '@mui/icons-material/Circle';

// Seamless loop moving the full width of the 200% background
const shimmerAnimation = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import RequireRole from "../../utils/RequireRole";
import { USERS_ADMIN_ROLES } from "../usersRoles/Guard";
import { ColorModeContext, tokens } from "../../theme";
import LogoutIcon from "@mui/icons-material/Logout";
import LanguageIcon from "@mui/icons-material/Language";
import CheckIcon from "@mui/icons-material/Check";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import CopyrightOutlinedIcon from "@mui/icons-material/CopyrightOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useAuth } from "../../contexts/AuthContext";

const Topbar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const langOpen = Boolean(langAnchorEl);

  const userName = user?.name || user?.firstName || user?.email?.split('@')[0] || "User";

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(' ').filter(p => p.length > 0);
    if (parts.length === 0) return "U";
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const userInitials = getInitials(userName);

  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleLangClick = (e) => setLangAnchorEl(e.currentTarget);
  const handleLangClose = () => setLangAnchorEl(null);

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    handleLangClose();
  };

  const handleLogout = async () => {
    // Close the dropdown immediately so the user sees the click register.
    // We don't await it — `logout` will either redirect (Keycloak) or hard-
    // navigate (bypass mode), so the menu state doesn't matter after this.
    handleClose();
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
      // Last-resort fallback: force a hard reset to the public homepage so
      // the user is never stranded in a "still signed in" state.
      window.location.assign(window.location.origin + '/');
    }
  };

  const pageTitles = {
    "/": { title: t('navigation.dashboard'), hasRegister: false },
    "/dashboard": { title: t('navigation.dashboard'), hasRegister: false },
    "/datasets": { title: t('navigation.datasets'), hasRegister: true, registerPath: "/datasets/registerDataset", registerText: t('buttons.registerDataset') },
    "/datasets/registerDataset": { title: t('navigation.datasets'), hasRegister: false },
    "/models": { title: t('navigation.models'), hasRegister: true, registerPath: "/models/registerModel", registerText: t('buttons.registerModel') },
    "/models/registerModel": { title: t('navigation.models'), hasRegister: false },
    "/sites": { title: t('navigation.sites', 'Client Sites'), hasRegister: false },
    "/clientApp": { title: t('navigation.clientApp'), hasRegister: false },
    "/serverApp": { title: t('navigation.serverApp'), hasRegister: false },
    "/configs": { title: t('navigation.configurations'), hasRegister: false },
    "/execute": { title: t('navigation.execute'), hasRegister: false },
    "/settings": { title: t('navigation.settings'), hasRegister: false },
    "/users-roles": { title: t('navigation.usersRoles', 'Users & Roles'), hasRegister: false },
    "/task": { title: t('navigation.task'), hasRegister: false },
  };

  let currentPage = pageTitles[location.pathname];
  if (!currentPage && location.pathname.startsWith("/documentation")) {
    currentPage = { title: "Documentation", hasRegister: false };
  }
  currentPage = currentPage || { title: t('navigation.dashboard'), hasRegister: false };

  const menuItemSx = {
    py: 0.9,
    px: 2,
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    color: 'var(--fx-text-primary)',
    fontSize: "0.8125rem",
    "&:hover": { backgroundColor: "rgba(27, 54, 93, 0.06)" },
    // Uniform, centered icon cell so every glyph (regardless of its intrinsic
    // width) sits in an identical column and the text starts at the same x.
    "& .menu-ico": {
      width: 20,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    "& .menu-ico svg": { fontSize: 17 },
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 3,
        minHeight: 55,
        background: "var(--fx-topbar-bg)",
        borderBottom: `1px solid ${theme.palette.divider}`,
        position: "sticky",
        top: 0,
        zIndex: 1000,
        py: 1
      }}
    >
      <Box display="flex" alignItems="center" gap={2}>
        <Typography
          sx={{
            color: 'var(--fx-text-primary)',
            fontWeight: 600,
            fontSize: "1rem",
            letterSpacing: "-0.005em",
          }}
        >
          {currentPage.title}
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        {/* Training Status pill */}
        {(() => {
          const {
            isRunning,
            isPaused,
            isCompleted: ctxIsCompleted,
            logs,
            trainingStatus,
            segmentationStatus,
            currentConfig,
            trainingProgress,
            segmentationProgress,
          } = useAppContext();

          const hasLogs = logs && logs.length > 0;
          const currentStatus = trainingStatus?.status || segmentationStatus || 'idle';
          const isTrainingRunning = isRunning || currentStatus === 'running' || currentStatus === 'in_progress';
          const isTrainingPaused = isPaused || currentStatus === 'paused';
          const isTrainingCompleted = ctxIsCompleted || currentStatus === 'completed';

          const shouldShow = (isTrainingRunning || isTrainingPaused || (isTrainingCompleted && hasLogs))
            && location.pathname !== '/execute';

          if (!shouldShow) return null;

          const isSegmentation = currentConfig?.dataset_config?.processing_type === "imaging_segmentation";
          const getProgressValue = () => {
            if (isSegmentation && currentStatus === 'completed') return 100;
            return isSegmentation
              ? (segmentationProgress?.percentage || 0)
              : (trainingProgress?.progress?.percentage || 0);
          };

          const effectiveProgress = getProgressValue();
          const showProgressBackground = effectiveProgress > 0 && !isTrainingCompleted;

          let statusText = t('execute.completed') || "Completed";
          let dotColor = colors.green?.[500] || "#16a34a";
          let showPulse = false;

          if (isTrainingRunning) {
            statusText = t('execute.running') || "Running";
            dotColor = colors.green?.[500] || "#16a34a";
            showPulse = true;
          } else if (isTrainingPaused) {
            statusText = t('execute.paused') || "Paused";
            dotColor = colors.amber?.[500] || "#d97706";
          }

          return (
            <Button
              onClick={() => navigate('/execute')}
              variant="outlined"
              sx={{
                borderColor: colors.grey[700],
                color: 'var(--fx-text-primary)',
                textTransform: 'none',
                borderRadius: '6px',
                px: 1.25,
                py: 0.4,
                minHeight: 30,
                fontWeight: 600,
                fontSize: "0.75rem",
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                backgroundColor: "var(--fx-paper)",
                position: 'relative',
                overflow: 'hidden',
                "&:hover": {
                  backgroundColor: "rgba(27, 54, 93, 0.04)",
                  borderColor: colors.primary[400],
                },
                "@keyframes pulse-dot": {
                  "0%, 100%": { transform: "scale(1)", opacity: 1 },
                  "50%": { transform: "scale(1.4)", opacity: 0.6 },
                },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: `${effectiveProgress}%`,
                  background: `linear-gradient(90deg, rgba(22,163,74,0.10), rgba(22,163,74,0.18), rgba(22,163,74,0.10))`,
                  backgroundSize: '200% 100%',
                  animation: showProgressBackground ? `${shimmerAnimation} 2.5s infinite linear` : 'none',
                  transition: 'width 0.6s cubic-bezier(0.65, 0, 0.35, 1)',
                  zIndex: 0,
                  display: (currentStatus === 'idle' || !currentStatus) ? 'none' : 'block',
                }}
              />
              <Box
                component="span"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  zIndex: 1,
                  "& svg": showPulse ? { animation: "pulse-dot 1.5s infinite ease-in-out" } : {},
                }}
              >
                <CircleIcon sx={{ fontSize: 8, color: dotColor }} />
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', position: 'relative', zIndex: 1, color: 'var(--fx-text-primary)' }}>
                {statusText}{effectiveProgress > 0 && isTrainingRunning ? ` · ${Math.round(effectiveProgress)}%` : ''}
              </Typography>
            </Button>
          );
        })()}

        <Tooltip title={colorMode.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} arrow>
          <IconButton
            onClick={colorMode.toggleColorMode}
            size="small"
            aria-label="Toggle color mode"
            sx={{
              color: colors.grey[500],
              "&:hover": { backgroundColor: "rgba(0,135,180,0.08)", color: colors.secondary[500] },
            }}
          >
            {colorMode.mode === 'dark'
              ? <LightModeOutlinedIcon sx={{ fontSize: 18 }} />
              : <DarkModeOutlinedIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>

        <Tooltip title={t('tooltips.language')} arrow>
          <IconButton
            onClick={handleLangClick}
            size="small"
            sx={{
              color: colors.grey[500],
              "&:hover": { backgroundColor: "rgba(27, 54, 93, 0.06)", color: 'var(--fx-text-primary)' },
            }}
          >
            <LanguageIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={langAnchorEl}
          open={langOpen}
          onClose={handleLangClose}
          sx={{
            mt: 0.5,
            "& .MuiPaper-root": {
              boxShadow: "0 8px 24px rgba(15,23,42,0.10)",
              borderRadius: 1.5,
              minWidth: 140,
              border: `1px solid ${theme.palette.divider}`,
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={() => handleLanguageChange('en')} sx={{ ...menuItemSx, gap: 1.25, backgroundColor: i18n.language === 'en' ? "rgba(27,54,93,0.06)" : "transparent" }}>
            <Box component="span" sx={{ fontSize: '1.15rem', lineHeight: 1, width: 22, textAlign: 'center' }}>🇬🇧</Box>
            <Box component="span" sx={{ flex: 1 }}>English</Box>
            {i18n.language === 'en' && <CheckIcon sx={{ fontSize: 16, color: colors.primary[400] }} />}
          </MenuItem>
          <MenuItem onClick={() => handleLanguageChange('fr')} sx={{ ...menuItemSx, gap: 1.25, backgroundColor: i18n.language === 'fr' ? "rgba(27,54,93,0.06)" : "transparent" }}>
            <Box component="span" sx={{ fontSize: '1.15rem', lineHeight: 1, width: 22, textAlign: 'center' }}>🇫🇷</Box>
            <Box component="span" sx={{ flex: 1 }}>Français</Box>
            {i18n.language === 'fr' && <CheckIcon sx={{ fontSize: 16, color: colors.primary[400] }} />}
          </MenuItem>
        </Menu>

        <Box>
          <IconButton
            onClick={handleClick}
            sx={{
              p: 0.25,
              ml: 0.5,
              border: `1.5px solid ${open ? colors.primary[400] : 'transparent'}`,
              transition: "border-color 0.15s",
              "&:hover": { backgroundColor: "transparent", borderColor: colors.primary[300] },
            }}
          >
            <Avatar
              sx={{
                bgcolor: '#0087B4',
                color: "#ffffff",
                width: 30,
                height: 30,
                fontSize: "0.75rem",
                fontWeight: 600,
                letterSpacing: "0.04em",
                border: '1.5px solid rgba(255,255,255,0.22)',
              }}
            >
              {userInitials}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            sx={{
              mt: 0.5,
              "& .MuiPaper-root": {
                boxShadow: "0 8px 24px rgba(15,23,42,0.10)",
                borderRadius: 1.5,
                minWidth: 240,
                border: `1px solid ${theme.palette.divider}`,
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
              <Avatar sx={{ bgcolor: '#1B365D', color: "#ffffff", width: 32, height: 32, fontSize: "0.75rem", fontWeight: 600 }}>
                {userInitials}
              </Avatar>
              <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Typography sx={{ fontWeight: 600, color: 'var(--fx-text-primary)', fontSize: "0.8125rem", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {userName}
                </Typography>
                <Typography sx={{ color: colors.grey[500], fontSize: "0.6875rem" }}>
                  Signed in
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderColor: theme.palette.divider, mb: 1 }} />

            <RequireRole roles={USERS_ADMIN_ROLES}>
              <MenuItem onClick={() => { handleClose(); navigate('/users-roles'); }} sx={menuItemSx}>
                <Box component="span" className="menu-ico"><GroupOutlinedIcon /></Box>
                {t('menu.usersRoles') || "Users & Roles"}
              </MenuItem>
            </RequireRole>
            <MenuItem onClick={() => { handleClose(); navigate('/settings'); }} sx={menuItemSx}>
              <Box component="span" className="menu-ico"><SettingsOutlinedIcon /></Box>
              {t('menu.settings') || "Settings"}
            </MenuItem>

            <Divider sx={{ borderColor: theme.palette.divider }} />

            <MenuItem onClick={() => navigate('/documentation/faq')} sx={menuItemSx}>
              <Box component="span" className="menu-ico"><HelpOutlineIcon /></Box>
              {t('menu.faq') || "FAQ"}
            </MenuItem>
            <MenuItem onClick={() => navigate('/documentation')} sx={menuItemSx}>
              <Box component="span" className="menu-ico"><DescriptionOutlinedIcon /></Box>
              {t('menu.documentation') || "Documentation"}
            </MenuItem>
            <MenuItem onClick={handleClose} sx={menuItemSx}>
              <Box component="span" className="menu-ico"><PrivacyTipOutlinedIcon /></Box>
              {t('menu.privacy') || "Privacy and Terms"}
            </MenuItem>
            <MenuItem onClick={handleClose} sx={menuItemSx}>
              <Box component="span" className="menu-ico"><CopyrightOutlinedIcon /></Box>
              {t('menu.copyright') || "Copyright"}
            </MenuItem>

            <Divider sx={{ borderColor: theme.palette.divider }} />

            <MenuItem onClick={handleLogout} sx={menuItemSx}>
              <Box component="span" className="menu-ico"><LogoutIcon /></Box>
              {t('menu.signOut')}
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </Box>
  );
};

export default Topbar;
